// libs
import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { FunnelOutline, GridOutline, SearchOutline } from '@lyearn/icons';
import { EmptyState } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
// components
import { Box } from '@lyearn/ui';
import { isFilterApplied } from '@/components/FilterManager/utils';

// utils
import getEmptyStateMessage from '../../utils/getEmptyStateMessage';
// types
import { AdminEmptyStateProps } from './types';

function AdminEmptyState({
  className,
  title,
  description,
  filter,
  search,
  showIllustrationIcon = true,
  clearSearch,
  clearFilters,
  Icon,
}: AdminEmptyStateProps) {
  const { t } = useTranslation();
  const isFiltersApplied = isFilterApplied(filter);
  const isSearchApplied = search.length > 0;
  const { title: resolvedTitle, description: resolvedDescription } = useMemo(
    () =>
      getEmptyStateMessage({
        title,
        description,
        filter,
        search,
        t,
      }),
    [description, filter, search, t, title],
  );

  const getEmptyStateIcon = useCallback(() => {
    if (!showIllustrationIcon) return null;
    if (isSearchApplied) return SearchOutline;
    if (isFiltersApplied) return FunnelOutline;
    if (Icon) return Icon;
    return GridOutline;
  }, [Icon, isFiltersApplied, isSearchApplied, showIllustrationIcon]);

  const primaryAction = useMemo(
    () =>
      isSearchApplied || isFiltersApplied
        ? {
            onClick: isSearchApplied ? clearSearch : clearFilters,
            id: isSearchApplied
              ? t('Common:EmptyState.ClearSearch')
              : t('Common:EmptyState.ClearFilters'),
            label: isSearchApplied
              ? t('Common:EmptyState.ClearSearch')
              : t('Common:EmptyState.ClearFilters'),
          }
        : undefined,
    [clearFilters, clearSearch, isFiltersApplied, isSearchApplied, t],
  );

  return (
    <Box className={cn('flex flex-1 flex-col items-center justify-center', className)}>
      <EmptyState
        Icon={getEmptyStateIcon()}
        description={resolvedDescription}
        primaryAction={primaryAction}
        title={resolvedTitle}
      />
    </Box>
  );
}

export default React.memo(AdminEmptyState);
